import { useEffect, useRef } from "react";

import type { ExcalidrawElement } from "@excalidraw/element/types";

import { layoutClips } from "../animation/layout";
import { animationPresets } from "../animation/presets";
import { createSpring } from "../animation/spring";

import type { TimelineSection } from "../animation/layout";
import type { ElementAnimation } from "../animation/types";

const MIN_DURATION_MS = 100;
const SNAP_MS = 50;

/** The sequence timeline: one section per screen (own 0-based clock, same
 * per-screen compile as Studio/export), one lane per animated element in
 * sequence order, one draggable bar per clip. Lane labels carry the
 * reorder arrows and the with/after-previous mode badge. */
export const ClipTimeline = ({
  sections,
  activeClip,
  playing,
  onSelect,
  onUpdate,
  onToggleMode,
  onMoveLane,
  onScrubStart,
  onScrub,
  getTimeMs,
}: {
  sections: TimelineSection[];
  activeClip: { elementId: string; index: number } | null;
  playing: boolean;
  onSelect: (elementId: string, index: number) => void;
  /** commit=false marks a transient mid-drag update (no undo capture) */
  onUpdate: (
    elementId: string,
    index: number,
    updates: Partial<ElementAnimation>,
    commit?: boolean,
  ) => void;
  onToggleMode: (element: ExcalidrawElement) => void;
  onMoveLane: (
    element: ExcalidrawElement,
    dir: -1 | 1,
    sectionId: string,
  ) => void;
  /** scrubbing previews ONE section's screen — the caller compiles that
   * section's members before sampling */
  onScrubStart: (section: TimelineSection) => void;
  onScrub: (timeMs: number) => void;
  getTimeMs: () => number;
}) => {
  const playheadRefs = useRef(new Map<string, HTMLDivElement>());
  // scale frozen at drag start so bars/ticks don't rescale under the cursor
  const dragTotalRef = useRef<{ id: string; total: number } | null>(null);
  // teardown for the in-flight scrub/drag — window listeners must not outlive
  // the component (e.g. the panel closing mid-drag via a remote scene change)
  const dragCleanupRef = useRef<(() => void) | null>(null);
  useEffect(() => () => dragCleanupRef.current?.(), []);

  const totalOf = (section: TimelineSection) =>
    dragTotalRef.current?.id === section.id
      ? dragTotalRef.current.total
      : Math.max(1000, section.totalMs);

  const setPlayhead = (sectionId: string, ms: number, total: number) => {
    const node = playheadRefs.current.get(sectionId);
    if (node) {
      node.style.left = `${Math.min(100, (ms / total) * 100)}%`;
    }
  };

  // follow playback with direct DOM updates — no React re-render per frame.
  // The dock plays the whole scene on one clock, so every section shows the
  // same time; exact for single-section scenes.
  // ponytail: per-screen playhead mapping if multi-screen dock playback ever
  // becomes screen-sequential
  useEffect(() => {
    if (!playing) {
      return;
    }
    let raf = requestAnimationFrame(function step() {
      const ms = getTimeMs();
      for (const section of sections) {
        setPlayhead(section.id, ms, totalOf(section));
      }
      raf = requestAnimationFrame(step);
    });
    return () => cancelAnimationFrame(raf);
  });

  const startScrub = (e: React.PointerEvent, section: TimelineSection) => {
    e.preventDefault();
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    if (rect.width === 0) {
      return;
    }
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    onScrubStart(section);
    const total = totalOf(section);
    const toMs = (clientX: number) =>
      Math.min(1, Math.max(0, (clientX - rect.left) / rect.width)) * total;
    let raf = 0;
    let lastX = e.clientX;
    const apply = () => {
      raf = 0;
      const ms = toMs(lastX);
      setPlayhead(section.id, ms, total);
      onScrub(ms);
    };
    apply();
    const onMove = (ev: PointerEvent) => {
      lastX = ev.clientX;
      if (!raf) {
        raf = requestAnimationFrame(apply);
      }
    };
    const cleanup = () => {
      if (raf) {
        cancelAnimationFrame(raf);
      }
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);
      dragCleanupRef.current = null;
    };
    const onUp = () => {
      if (raf) {
        cancelAnimationFrame(raf);
        raf = 0;
        apply();
      }
      cleanup();
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);
    dragCleanupRef.current = cleanup;
  };

  const startDrag = (
    e: React.PointerEvent,
    section: TimelineSection,
    elementId: string,
    clip: ElementAnimation,
    index: number,
    mode: "move" | "resize",
  ) => {
    e.preventDefault();
    e.stopPropagation();
    onSelect(elementId, index);
    if (mode === "resize" && clip.spring) {
      return;
    }
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    const startX = e.clientX;
    const orig = mode === "move" ? clip.delay : clip.duration;
    // freeze the scale at drag start so resizing doesn't rescale under the cursor
    const total = totalOf(section);
    const laneEl = (e.currentTarget as HTMLElement).closest(
      ".clip-timeline__lane",
    ) as HTMLElement | null;
    const pxPerMs = (laneEl?.clientWidth ?? 1) / total;
    dragTotalRef.current = { id: section.id, total };
    const valueAt = (clientX: number) => {
      const raw = orig + (clientX - startX) / pxPerMs;
      return mode === "move"
        ? Math.max(0, Math.round(raw))
        : Math.max(MIN_DURATION_MS, Math.round(raw));
    };
    let raf = 0;
    let lastX = startX;
    // a plain click (no movement) must not mutate the clip — only commit once
    // the pointer actually dragged
    let moved = false;
    // raw (unsnapped) while dragging so the bar tracks the cursor 1:1;
    // coalesced to one scene update per frame
    const apply = () => {
      raf = 0;
      const value = valueAt(lastX);
      onUpdate(
        elementId,
        index,
        mode === "move" ? { delay: value } : { duration: value },
        false,
      );
    };
    const onMove = (ev: PointerEvent) => {
      lastX = ev.clientX;
      if (Math.abs(lastX - startX) > 2) {
        moved = true;
      }
      if (moved && !raf) {
        raf = requestAnimationFrame(apply);
      }
    };
    const cleanup = () => {
      if (raf) {
        cancelAnimationFrame(raf);
      }
      dragTotalRef.current = null;
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);
      dragCleanupRef.current = null;
    };
    const onUp = (ev: PointerEvent) => {
      cleanup();
      if (!moved) {
        return;
      }
      // snap only on release; this is the one committed (undoable) update.
      // pointercancel carries default (0,0) coordinates — commit the last
      // dragged position instead of a garbage valueAt(0)
      const clientX = ev.type === "pointercancel" ? lastX : ev.clientX;
      const snapped = Math.round(valueAt(clientX) / SNAP_MS) * SNAP_MS;
      const value =
        mode === "move"
          ? Math.max(0, snapped)
          : Math.max(MIN_DURATION_MS, snapped);
      onUpdate(
        elementId,
        index,
        mode === "move" ? { delay: value } : { duration: value },
      );
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);
    dragCleanupRef.current = cleanup;
  };

  return (
    <div className="clip-timeline">
      {sections.map((section, si) => {
        const total = totalOf(section);
        const tickStep = total <= 2000 ? 500 : total <= 6000 ? 1000 : 2000;
        const ticks: number[] = [];
        for (let t = 0; t <= total; t += tickStep) {
          ticks.push(t);
        }
        const title = section.frame
          ? section.frame.name || `Screen ${si + 1}`
          : sections.length > 1
          ? "Canvas"
          : null;
        return (
          <div key={section.id} className="clip-timeline__section">
            {title && (
              <div className="clip-timeline__section-header">
                <span>{title}</span>
                <span className="clip-timeline__section-length">
                  {(section.totalMs / 1000).toFixed(1)}s
                </span>
              </div>
            )}
            <div
              className="clip-timeline__ruler"
              title="Drag to scrub this screen"
              onPointerDown={(e) => startScrub(e, section)}
            >
              {ticks.map((t) => (
                <span
                  key={t}
                  className="clip-timeline__tick"
                  style={{ left: `${(t / total) * 100}%` }}
                >
                  {t >= 1000 ? `${t / 1000}s` : `${t}`}
                </span>
              ))}
            </div>
            <div
              className="clip-timeline__tracks"
              // lane cell width: one segment per 0.5s of timeline
              style={
                { "--seg-w": `${(500 / total) * 100}%` } as React.CSSProperties
              }
            >
              <div
                className="clip-timeline__playhead"
                ref={(node) => {
                  if (node) {
                    playheadRefs.current.set(section.id, node);
                  } else {
                    playheadRefs.current.delete(section.id);
                  }
                }}
              />
              {section.lanes.map((lane, li) => {
                const bars = layoutClips(lane.clips);
                return (
                  <div
                    key={lane.element.id}
                    className="clip-timeline__track"
                    onPointerDown={() => onSelect(lane.element.id, 0)}
                  >
                    <span
                      className="clip-timeline__label"
                      title={lane.element.type}
                    >
                      <button
                        className="clip-timeline__lane-btn"
                        title="Play earlier in the sequence"
                        onClick={(e) => {
                          e.stopPropagation();
                          onMoveLane(lane.element, -1, section.id);
                        }}
                      >
                        ‹
                      </button>
                      <span className="clip-timeline__lane-num">{li + 1}</span>
                      <button
                        className="clip-timeline__lane-btn"
                        title="Play later in the sequence"
                        onClick={(e) => {
                          e.stopPropagation();
                          onMoveLane(lane.element, 1, section.id);
                        }}
                      >
                        ›
                      </button>
                      {li > 0 && (
                        <button
                          className="clip-timeline__lane-btn clip-timeline__lane-mode"
                          title={
                            lane.mode === "with"
                              ? "Starts with the previous element — click to play after it finishes"
                              : "Starts after the previous element finishes — click to play alongside it"
                          }
                          onClick={(e) => {
                            e.stopPropagation();
                            onToggleMode(lane.element);
                          }}
                        >
                          {lane.mode === "with" ? "∥" : "→"}
                        </button>
                      )}
                      <span className="clip-timeline__lane-name">
                        {lane.element.type}
                      </span>
                    </span>
                    <div className="clip-timeline__lane">
                      {ticks.map((t) => (
                        <span
                          key={t}
                          className="clip-timeline__gridline"
                          style={{ left: `${(t / total) * 100}%` }}
                        />
                      ))}
                      {lane.clips.map((clip, i) => (
                        <div
                          key={i}
                          className={`clip-timeline__bar${
                            activeClip?.elementId === lane.element.id &&
                            activeClip.index === i
                              ? " clip-timeline__bar--active"
                              : ""
                          }`}
                          style={{
                            left: `${
                              ((lane.startMs + bars[i].start) / total) * 100
                            }%`,
                            width: `${(bars[i].width / total) * 100}%`,
                          }}
                          title={`${
                            animationPresets[clip.preset]?.name ?? clip.preset
                          } — delay ${clip.delay}ms · duration ${
                            clip.spring
                              ? Math.round(createSpring(clip.spring).durationMs)
                              : clip.duration
                          }ms — drag to change delay, drag right edge for duration`}
                          onPointerDown={(e) =>
                            startDrag(
                              e,
                              section,
                              lane.element.id,
                              clip,
                              i,
                              "move",
                            )
                          }
                        >
                          {bars[i].infinite && <span>∞</span>}
                          {!clip.spring && (
                            <span
                              className="clip-timeline__handle"
                              onPointerDown={(e) =>
                                startDrag(
                                  e,
                                  section,
                                  lane.element.id,
                                  clip,
                                  i,
                                  "resize",
                                )
                              }
                            />
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
};
